import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Queue } from '../../types';
import PayloadFieldSelector from './PayloadFieldSelector';

interface QueueEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    description: string;
    display_payload_fields: string[];
  }) => Promise<void>;
  queue: Queue | null;
}

/**
 * QueueEditModal - Modal for editing queue name and description
 *
 * Similar to ProjectFormModal, this provides a simple form for editing
 * the queue's basic information (name and description).
 *
 * Props:
 * - open: Whether the modal is visible
 * - onClose: Callback when modal is closed
 * - onSave: Async callback with the updated name and description
 * - queue: The queue being edited
 */
const QueueEditModal: React.FC<QueueEditModalProps> = ({
  open,
  onClose,
  onSave,
  queue
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AC-1482: PayloadFieldSelector is mounted with `hideSave` so its selection
  // submits together with name + description through the modal's single
  // `Save Changes` button. This avoids the dual-save trap where users
  // changed columns and clicked `Save Changes` expecting it to persist
  // everything.
  const [selectedFields, setSelectedFields] = useState<string[]>(
    queue?.display_payload_fields ?? [],
  );

  useEffect(() => {
    if (queue) {
      setName(queue.name);
      setDescription(queue.description || '');
      setSelectedFields(queue.display_payload_fields ?? []);
    } else {
      setName('');
      setDescription('');
      setSelectedFields([]);
    }
    setError(null);
  }, [queue, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Queue name is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        display_payload_fields: selectedFields,
      });
      onClose();
    } catch (err) {
      setError('Failed to save queue');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">
            Edit Queue
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="mb-4">
            <label htmlFor="queue-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Queue Name
            </label>
            <input
              id="queue-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter queue name"
              required
              data-testid="queue-name-input"
            />
          </div>
          <div className="mb-6">
            <label htmlFor="queue-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              id="queue-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter queue description"
              rows={3}
              data-testid="queue-description-input"
            />
          </div>
          {/*
            AC-1480: Payload field column configuration. The selector owns its
            own Save button (uses `useUpdateQueue` to persist
            `display_payload_fields`) and runs INDEPENDENTLY of the modal's
            "Save Changes" submit, which only saves name + description.
            We control the selection state at this level so we can re-seed
            from `queue.display_payload_fields` on queue change.
          */}
          {queue && (
            <div className="mb-6">
              <PayloadFieldSelector
                queueId={queue.queue_id}
                value={selectedFields}
                onChange={setSelectedFields}
                hideSave
              />
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-button"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QueueEditModal;
